import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function translatePaidWith(paidWith: string) {
  switch (paidWith) {
    case "cash":
      return "Efectivo";
    case "card":
      return "Tarjeta";
    default:
      return paidWith;
  }
}

/**
 * Safely concatenates first and last names with proper null/undefined checks
 * @param firstName - The first name (can be null, undefined, or empty string)
 * @param lastName - The last name (can be null, undefined, or empty string)
 * @returns A properly formatted full name or null if both names are invalid
 */
export function formatUserName(
  firstName?: string | null,
  lastName?: string | null,
): string | null {
  // Normalize empty strings and whitespace-only strings to null
  const normalizedFirstName = firstName?.trim() || null;
  const normalizedLastName = lastName?.trim() || null;

  // If both names are invalid, return null
  if (!normalizedFirstName && !normalizedLastName) {
    return null;
  }

  // If only one name is valid, return that name
  if (!normalizedFirstName) {
    return normalizedLastName;
  }

  if (!normalizedLastName) {
    return normalizedFirstName;
  }

  // Both names are valid, concatenate them
  return `${normalizedFirstName} ${normalizedLastName}`;
}

/**
 * Gets the start of the current week (Monday at 00:00:00)
 *
 * @returns ISO string representation of the Monday at 00:00:00 of the current week
 *
 * @example
 * ```typescript
 * const weekStart = getWeekStart();
 * console.log(weekStart); // "2024-01-15T00:00:00.000Z" (if current week starts on Jan 15)
 * ```
 *
 * @remarks
 * - Week starts on Monday (ISO 8601 standard)
 * - If today is Sunday, it returns the previous Monday
 * - Time is set to 00:00:00.000 (start of day)
 * - Returns date in UTC timezone
 */
export const getWeekStart = (): string => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - daysToSubtract);
  weekStart.setHours(0, 0, 0, 0);

  return weekStart.toISOString();
};

/**
 * Gets the timestamp for 24 hours ago from the current time
 *
 * @returns ISO string representation of the date and time 24 hours ago
 *
 * @example
 * ```typescript
 * const last24Hours = getLast24Hours();
 * console.log(last24Hours); // "2024-01-15T14:30:00.000Z" (if current time is Jan 16 14:30)
 * ```
 *
 * @remarks
 * - Returns exactly 24 hours (86400000 milliseconds) before the current time
 * - Preserves the exact time, not just the date
 * - Returns date in UTC timezone
 * - Useful for filtering recent data within the last 24 hours
 */
export const getLast24Hours = (): string => {
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return twentyFourHoursAgo.toISOString();
};

/**
 * Gets the start of the current day (00:00:00)
 *
 * @returns ISO string representation of the current day at 00:00:00.000
 *
 * @example
 * ```typescript
 * const dayStart = getCurrentDayStart();
 * console.log(dayStart); // "2024-01-16T00:00:00.000Z" (start of current day)
 * ```
 *
 * @remarks
 * - Returns the current date with time set to 00:00:00.000 (start of day)
 * - Returns date in UTC timezone
 * - Useful for filtering data from the beginning of the current day
 */
export const getCurrentDayStart = (): string => {
  const now = new Date();
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  return dayStart.toISOString();
};

/**
 * Converts a given Date object to a new Date object in UTC, preserving the same year, month, day, hour, minute, second, and millisecond values as in UTC.
 * @param date The Date object to convert.
 * @returns A new Date object representing the same moment in UTC.
 */
export function toUTCDate(date: Date): Date {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    ),
  );
}

/**
 * Gets the UTC start and end of today as ISO strings.
 * @returns An object with 'from' and 'to' properties as ISO strings in UTC.
 */
export function getTodayRangeInUTC() {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfDay = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999,
  );
  return {
    from: startOfDay.toISOString(),
    to: endOfDay.toISOString(),
  };
}

export function getThisWeekRangeInUTC() {
  const now = new Date();
  const day = now.getDay(); // 0 (Sun) to 6 (Sat)
  const diff = day === 0 ? -6 : 1 - day; // Shift back to Monday
  const startOfWeek = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + diff,
  );
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(endOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  return {
    from: startOfWeek.toISOString(),
    to: endOfWeek.toISOString(),
  };
}

export function getThisMonthRangeInUTC() {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  startOfMonth.setHours(0, 0, 0, 0);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);
  return {
    from: startOfMonth.toISOString(),
    to: endOfMonth.toISOString(),
  };
}

/**
 * Returns the date range (from and to) in UTC ISO string format for the specified period.
 *
 * @param {"day" | "week" | "month"} [period] - The period for which to get the date range.
 *   - "day": Returns the range for today.
 *   - "week": Returns the range for the current week (Monday to Sunday).
 *   - "month": Returns the range for the current month.
 *   - If not specified or unrecognized, defaults to "day".
 * @returns {{ fromDate: string, toDate: string }} An object containing the start and end dates in ISO string format.
 */
export const getDateRange = (period?: "day" | "week" | "month") => {
  let range: { from: string; to: string };

  switch (period) {
    case "day":
      range = getTodayRangeInUTC();
      break;
    case "week":
      range = getThisWeekRangeInUTC();
      break;
    case "month":
      range = getThisMonthRangeInUTC();
      break;
    default:
      // Default to current day
      range = getTodayRangeInUTC();
  }

  return {
    fromDate: range.from,
    toDate: range.to,
  };
};

/**
 * Returns today's start and end range in UTC ISO strings for El Salvador timezone (America/El_Salvador).
 * Also returns the local date string (YYYY-MM-DD) for use in count endpoints.
 * Assumes El Salvador is UTC-6 year-round (no DST).
 */
export function getTodayRangeInElSalvador() {
  const timeZone = "America/El_Salvador";
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const year = Number(
    parts.find((p) => p.type === "year")?.value || new Date().getFullYear(),
  );
  const month = Number(
    parts.find((p) => p.type === "month")?.value || new Date().getMonth() + 1,
  );
  const day = Number(
    parts.find((p) => p.type === "day")?.value || new Date().getDate(),
  );

  // El Salvador local midnight corresponds to 06:00 UTC of the same day (UTC-6)
  const startUTC = new Date(Date.UTC(year, month - 1, day, 6, 0, 0, 0));
  // Local 23:59:59.999 corresponds to 05:59:59.999 of the next day UTC
  const endUTC = new Date(Date.UTC(year, month - 1, day + 1, 5, 59, 59, 999));

  const pad = (n: number) => String(n).padStart(2, "0");
  const localDate = `${year}-${pad(month)}-${pad(day)}`;

  return {
    from: startUTC.toISOString(),
    to: endUTC.toISOString(),
    date: localDate,
  };
}

export const formatDateToFriendlyFormat = (dateStr: string) => {
  if (!dateStr) return "";
  // Expecting format "YYYY-MM-DD"
  const [year, month, day] = dateStr.split("-");
  if (!year || !month || !day) return dateStr;
  // Month names in Spanish abbreviated
  const monthNames = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  const monthIndex = parseInt(month, 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return dateStr;
  // Format as "Jul 29, 2025"
  return `${monthNames[monthIndex]} ${parseInt(day, 10)}, ${year}`;
};
